import { sendAnalyticsEvent, UnknownEvent } from '@faststore/sdk'
import type { KeyboardEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  useAuthenticatedUserToken,
  useUserToken,
} from 'src/sdk/analytics/hooks/useUserToken'
// import useSearchHistory from 'src/sdk/search/useSearchHistory'
import type { PromoHit, TopicHit } from './algolia'
import {
  MIN_ALGOLIA_QUERY_LENGTH,
  SEARCH_DEBOUNCE_MS,
} from './algoliaSearchConfig'
import { emptyTypingState } from './AutocompletePanel'
import AutocompleteShell from './AutocompleteShell'
import type { SearchbarPanelContextValue } from './SearchBarPanelContext'
import { SearchbarPanelProvider } from './SearchBarPanelContext'
import type { TypingStateResult } from './searchbarQueries'
import {
  fetchEmptyStateMultiquery,
  fetchTypingMultiquery,
} from './searchbarQueries'
import { customFormatSearchPath } from './utils'

// TODO(algolia-searchbar): as-is ha già search desktop+mobile (due slot Header).
const CustomAutocomplete = () => {
  const rootRef = useRef<HTMLDivElement>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const debouncedQueryRef = useRef('')
  const isOpenRef = useRef(false)
  const [isOpen, setIsOpen] = useState(false)
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [promoHit, setPromoHit] = useState<PromoHit | null>(null)
  const [topicHits, setTopicHits] = useState<TopicHit[]>([])
  const [typing, setTyping] = useState<TypingStateResult>(emptyTypingState)
  const [typingReady, setTypingReady] = useState(false)
  const emptyStateLoadedRef = useRef(false)
  const userToken = useUserToken()
  const authenticatedUserToken = useAuthenticatedUserToken()
  // const {
  //   searchHistory,
  //   deleteFromSearchHistory,
  //   clearSearchHistory,
  //   addToSearchHistory,
  // } = useSearchHistory()

  isOpenRef.current = isOpen

  const isTyping = debouncedQuery.trim().length >= MIN_ALGOLIA_QUERY_LENGTH

  const commitDebouncedQuery = (value: string) => {
    setDebouncedQuery(value)
    debouncedQueryRef.current = value
  }

  const resetToEmpty = () => {
    clearTimeout(debounceTimerRef.current)
    commitDebouncedQuery('')
  }

  const scheduleTypingQuery = (value: string) => {
    clearTimeout(debounceTimerRef.current)

    const trimmed = value.trim()

    // Cancella / input vuoto: sempre empty
    if (trimmed.length === 0) {
      resetToEmpty()
      return
    }

    // Sotto soglia: aggiorna React solo se stavi in typing (torna empty)
    if (trimmed.length < MIN_ALGOLIA_QUERY_LENGTH) {
      if (debouncedQueryRef.current.trim().length >= MIN_ALGOLIA_QUERY_LENGTH) {
        commitDebouncedQuery(value)
      }

      return
    }

    debounceTimerRef.current = setTimeout(() => {
      commitDebouncedQuery(value)
    }, SEARCH_DEBOUNCE_MS)
  }

  const isResetControl = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false

    return Boolean(
      target.closest('.ais-Autocomplete-reset, button[type="reset"]')
    )
  }

  useEffect(() => {
    return () => clearTimeout(debounceTimerRef.current)
  }, [])

  // Empty state: multiquery promo(1) + topic(N), una volta per sessione panel
  useEffect(() => {
    if (!isOpen || emptyStateLoadedRef.current) return
    // const input = rootRef.current?.querySelector('input')
    // if (Boolean(input?.value)) return

    let cancelled = false

    void (async () => {
      const { promoHit: nextPromo, topicHits: nextTopics } =
        await fetchEmptyStateMultiquery(userToken)

      if (cancelled) return

      setPromoHit(nextPromo)
      setTopicHits(nextTopics)
      emptyStateLoadedRef.current = true
    })()

    return () => {
      cancelled = true
    }
  }, [isOpen])

  // Digitazione: multiquery dopo debounce (≥3 char)
  useEffect(() => {
    const query = debouncedQuery.trim()

    if (query.length < MIN_ALGOLIA_QUERY_LENGTH) {
      setTyping(emptyTypingState(query))
      setTypingReady(false)
      return
    }

    let cancelled = false

    setTypingReady(false)

    void (async () => {
      const next = await fetchTypingMultiquery(query, userToken)

      if (cancelled) return

      setTyping(next)
      setTypingReady(true)
    })()

    return () => {
      cancelled = true
    }
  }, [debouncedQuery])

  useEffect(() => {
    if (
      debouncedQueryRef.current.length >= MIN_ALGOLIA_QUERY_LENGTH &&
      userToken
    ) {
      sendAnalyticsEvent<UnknownEvent>({
        name: 'search',
        params: {
          event: 'ga4_event',
          event_name: 'search',
          search_trigger: 'live',
          nbHits: typing.productNbHits,
          queryID: typing.productQueryID,
          algolia_user_token: userToken,
          ...(authenticatedUserToken && {
            algolia_authenticated_user_token: authenticatedUserToken,
          }),
          search_term: typing.query,
        },
      })
    }
  }, [typing, userToken, authenticatedUserToken])

  const closeSearch = useCallback(() => {
    setIsOpen(false)
    const input = rootRef.current?.querySelector('input')

    input?.dispatchEvent(
      new window.KeyboardEvent('keydown', {
        key: 'Escape',
        code: 'Escape',
        bubbles: true,
        cancelable: true,
      })
    )
    input?.blur()
  }, [])

  // TODO: workaround EXPERIMENTAL: Invio nativo non naviga a /s (solo refine).
  const handleSearch = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') return

    const { target } = event

    if (
      !(target instanceof HTMLInputElement) ||
      !target.value ||
      target.value.trim().length === 0
    )
      return

    event.preventDefault()
    event.stopPropagation()

    sendAnalyticsEvent<UnknownEvent>({
      name: 'search',
      params: {
        event: 'ga4_event',
        event_name: 'search',
        search_trigger: 'submit',
        nbHits: typing.productNbHits,
        queryID: typing.productQueryID,
        algolia_user_token: userToken,
        ...(authenticatedUserToken && {
          algolia_authenticated_user_token: authenticatedUserToken,
        }),
        search_term: typing.query,
      },
    })
    // addToSearchHistory(target.value)
    window.location.href = customFormatSearchPath(target.value)
  }

  const panelContextValue = useMemo<SearchbarPanelContextValue>(
    () => ({
      promoHit,
      topicHits,
      typing,
      isTyping,
      typingReady,
      onSelectItem: closeSearch,
      // searchHistory,
      // deleteFromSearchHistory,
      // clearSearchHistory,
    }),
    [
      promoHit,
      topicHits,
      typing,
      isTyping,
      typingReady,
      closeSearch,
      // searchHistory,
      // deleteFromSearchHistory,
      // clearSearchHistory,
    ]
  )

  return (
    <SearchbarPanelProvider value={panelContextValue}>
      <div
        ref={rootRef}
        className="group relative w-full"
        onKeyDownCapture={handleSearch}
        onInputCapture={(event) => {
          if (event.target instanceof HTMLInputElement) {
            scheduleTypingQuery(event.target.value)
          }
        }}
        onClickCapture={(event) => {
          if (isResetControl(event.target)) {
            resetToEmpty()
          }
        }}
        onFocusCapture={() => {
          if (!isOpenRef.current) setIsOpen(true)

          const value = rootRef.current?.querySelector('input')?.value ?? ''

          if (value.trim().length >= MIN_ALGOLIA_QUERY_LENGTH) {
            commitDebouncedQuery(value)
          }
        }}
      >
        {isOpen && (
          <div
            className="fixed inset-0 z-[50] bg-[rgba(0,0,0,0.5)] overlay-visible"
            onMouseDown={(event) => {
              event.preventDefault()
              closeSearch()
            }}
          />
        )}

        <div className="relative z-[60]">
          <AutocompleteShell isOpen={isOpen} />
        </div>
      </div>
    </SearchbarPanelProvider>
  )
}

export default CustomAutocomplete
